import React, { useState, useEffect } from 'react';
import { Paper, Typography, IconButton, Box, LinearProgress, Switch, FormControlLabel, Stack, Button } from '@mui/material';
import { PlayArrow, Pause, Refresh, FullscreenExit, Timer } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export default function FocusTimer({ onZenToggle, isZenMode }) {
    const { t } = useTranslation();
    const WORK_TIME = 25 * 60;
    const BREAK_TIME = 5 * 60;

    const [timeLeft, setTimeLeft] = useState(WORK_TIME);
    const [isActive, setIsActive] = useState(false);
    const [isWork, setIsWork] = useState(true);
    const [minimized, setMinimized] = useState(false);

    useEffect(() => {
        let interval = null;
        if (isActive && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft((prev) => prev - 1);
            }, 1000);
        } else if (timeLeft === 0) {
            // Timer finished
            setIsActive(false);
            new Audio('/notification.mp3').play().catch(() => { }); // Simple notification sound attempt
            if (isWork) {
                // Work finished, start break?
                // For now just stop and let user switch
            }
        }
        return () => clearInterval(interval);
    }, [isActive, timeLeft, isWork]);

    const toggleTimer = () => setIsActive(!isActive);
    const resetTimer = () => {
        setIsActive(false);
        setTimeLeft(isWork ? WORK_TIME : BREAK_TIME);
    };

    const handleModeSwitch = () => {
        const nextModeIsWork = !isWork;
        setIsWork(nextModeIsWork);
        setIsActive(false);
        setTimeLeft(nextModeIsWork ? WORK_TIME : BREAK_TIME);
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const progress = 100 - (timeLeft / (isWork ? WORK_TIME : BREAK_TIME)) * 100;

    return (
        <Paper
            component={motion.div}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            elevation={6}
            sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1300,
                width: minimized ? 60 : 280,
                height: minimized ? 60 : 'auto',
                overflow: 'hidden',
                borderRadius: 4,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider'
            }}
        >
            {minimized ? (
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    onClick={() => setMinimized(false)}
                    sx={{ cursor: 'pointer', position: 'relative' }}
                >
                    <Timer color={isWork ? "primary" : "secondary"} />
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4 }}
                        color={isWork ? "primary" : "secondary"}
                    />
                </Stack>
            ) : (
                <Box p={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                            {isWork ? t('focus_mode') || "FOCUS" : t('break_mode') || "BREAK"}
                        </Typography>
                        <Box>
                            <IconButton size="small" onClick={() => setMinimized(true)}>
                                <FullscreenExit fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    <Typography variant="h3" fontWeight="bold" textAlign="center" my={2} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatTime(timeLeft)}
                    </Typography>

                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ mb: 3, height: 8, borderRadius: 4 }}
                        color={isWork ? "primary" : "secondary"}
                    />

                    <Box display="flex" justifyContent="center" gap={1} mb={2}>
                        <IconButton
                            onClick={toggleTimer}
                            color={isWork ? "primary" : "secondary"}
                            sx={{ bgcolor: 'action.hover', p: 1.5 }}
                        >
                            {isActive ? <Pause /> : <PlayArrow />}
                        </IconButton>
                        <IconButton onClick={resetTimer} size="small">
                            <Refresh />
                        </IconButton>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} pt={2} borderTop={1} borderColor="divider">
                        <FormControlLabel
                            control={<Switch size="small" checked={isZenMode} onChange={(e) => onZenToggle(e.target.checked)} />}
                            label={<Typography variant="caption">{t('zen_mode') || "Zen Mode"}</Typography>}
                        />
                        <Button size="small" onClick={handleModeSwitch} color="inherit">
                            {isWork ? "Start Break" : "Start Focus"}
                        </Button>
                    </Box>
                </Box>
            )}
        </Paper>
    );
}
